<script setup lang="ts">
import { FALLBACK_DOWNLOAD_LINKS, getAppDownloadLinks, normalizeDownloadLinks } from '@/api/download'
import {
  getSurveyExhibitionHall,
  type SurveyExhibitionHallResponse,
  type SurveyListItem,
  type SurveyRecentTrader,
} from '@/api/survey'

type SurveyExpoListPageAsyncData = {
  downloadLinks: ReturnType<typeof normalizeDownloadLinks>
  expoSurveyListData: SurveyExhibitionHallResponse['data'] | null
}

const { locale, t } = useAppI18n()
const [titleText, descriptionText] = await Promise.all([
  getSeoLocaleMessage(locale, '84111'),
  getSeoLocaleMessage(locale, '84133'),
])
if (import.meta.server) {
  useSeoMeta({
    title: titleText,
    description: descriptionText,
  })
}

/** 替换占位符后的 SEO 标题。 */
const seoTitle = computed(() => t('84111', ['EXPO']))

useHead(() => ({
  title: seoTitle.value,
  meta: [
    {
      name: 'description',
      content: t('84133'),
    },
  ],
}))

const DEFAULT_PAGE_SIZE = 20

/** 组件入参，仅暴露当前页码，避免内容组件感知 `/page/1` 假404策略。 */
const props = defineProps<{
  currentPage: number
}>()
const route = useRoute()

/** 将页码 props 收敛成 computed，确保分页切换时依赖统一更新。 */
const currentPage = computed(() => props.currentPage)

type BreadcrumbSourceItem = {
  route: string
  label?: string
  labelKey?: string
  color?: string
}

/** 从路由段读取当前展会实勘 slug / surveyId。 */
const getRouteExpoSlug = computed(() => {
  const expoSlug = route.params.slug
  if (Array.isArray(expoSlug)) {
    return expoSlug[0] || ''
  }

  return typeof expoSlug === 'string' ? expoSlug : ''
})

/** 客户端缓存的上一页来源面包屑，新开页面时从 localStorage 读取。 */
const sourceBreadcrumb = ref<BreadcrumbSourceItem | null>(null)
const { getBreadcrumb } = useLsBreadcrumb()
/** 读取当前详情页对应的来源面包屑。 */
const refreshSourceBreadcrumb = () => {
  sourceBreadcrumb.value = getBreadcrumb(getRouteExpoSlug.value)
}

/** 按当前语言解析第二级面包屑文案，优先使用缓存中的翻译 key。 */
const resolveSourceBreadcrumbLabel = (breadcrumb: BreadcrumbSourceItem | null) => {
  if (!breadcrumb) {
    return ''
  }

  if (breadcrumb.labelKey) {
    return t(breadcrumb.labelKey)
  }

  return breadcrumb.label || ''
}

/** 第二级面包屑仅在客户端读到来源记录后才渲染。 */
const surveySourceBreadcrumb = computed<BreadcrumbSourceItem | null>(() => sourceBreadcrumb.value)

/** 当前页面的面包屑配置。 */
const surveyBreadcrumbItems = computed(() => {
  const breadcrumbItems: Array<{ label: string; route?: string; color?: string }> = [
    {
      label: t('82520'),
      route: '/',
    },
  ]

  if (surveySourceBreadcrumb.value?.route) {
    breadcrumbItems.push({
      label: resolveSourceBreadcrumbLabel(surveySourceBreadcrumb.value),
      route: surveySourceBreadcrumb.value.route,
    })
  }

  breadcrumbItems.push({
    label: t('82521'),
  })

  return breadcrumbItems
})

/** 统一按当前路由并发加载页面聚合数据与下载地址，首屏 SSR、站内导航 CSR 复用同一入口。 */
const loadExpoSurveyListData = async (): Promise<SurveyExpoListPageAsyncData> => {
  const [surveyExhibitionHallResponse, downloadLinksResponse] = await Promise.all([
    getSurveyExhibitionHall({
      urlPath: getRouteExpoSlug.value,
      index: currentPage.value,
      size: DEFAULT_PAGE_SIZE,
    }),
    getAppDownloadLinks().catch(() => null),
  ])

  return {
    downloadLinks: normalizeDownloadLinks(downloadLinksResponse),
    expoSurveyListData: surveyExhibitionHallResponse?.data ?? null,
  }
}

/** 获取页面聚合数据，并在路由参数变化时自动重新请求。 */
const { data: expoSurveyListData } = await useAsyncData(
  () => `survey-expo-page:${getRouteExpoSlug.value}:${currentPage.value}`,
  loadExpoSurveyListData,
  {
    default: () => ({
      downloadLinks: FALLBACK_DOWNLOAD_LINKS,
      expoSurveyListData: null,
    }),
    watch: [getRouteExpoSlug, currentPage],
  },
)

/** 列表总数由当前聚合响应直接派生。 */
const surveyListTotal = computed<number>(() =>
  Number(expoSurveyListData.value?.expoSurveyListData?.exhibitionHall?.total ?? 0),
)

/** 列表数据由当前聚合响应直接派生。 */
const surveyListItems = computed<SurveyListItem[]>(
  () => expoSurveyListData.value?.expoSurveyListData?.exhibitionHall?.items ?? [],
)

const is404 = ref(false)
if (surveyListItems.value.length === 0) {
  is404.value = true
}

/** 右侧最近实勘交易商列表。 */
const recentTraders = computed<SurveyRecentTrader[]>(
  () => expoSurveyListData.value?.expoSurveyListData?.recentTrader ?? [],
)

/** 右侧下载卡片使用的 App 下载地址。 */
const downloadLinks = computed(() => expoSurveyListData.value?.downloadLinks ?? FALLBACK_DOWNLOAD_LINKS)

/** 当前接口分页大小固定为 20。 */
const pageSize = computed(() => DEFAULT_PAGE_SIZE)

/** 分页组件内部使用的路由模板，只传 slug 与页码占位符。 */
const pageRoute = computed(() => `/inspections/expo/${getRouteExpoSlug.value}/page/{idx}`)

/** 页脚高度，SSR 用默认值兜底，客户端挂载后动态测量。 */
const footerHeight = ref(315)

onMounted(() => {
  refreshSourceBreadcrumb()
  // 动态获取页脚 DOM 高度，使列表区最小高度精确填满视口剩余空间
  const footerEl = document.querySelector('.wiki-bit-pc-bootom-map') as HTMLElement | null
  if (footerEl) {
    footerHeight.value = footerEl.offsetHeight
  } else {
    footerHeight.value = 0
  }
})

watch(getRouteExpoSlug, () => {
  if (!import.meta.client) {
    return
  }

  refreshSourceBreadcrumb()
})
</script>

<template>
  <CommonNotFound v-if="is404" />
  <div
    v-else
    class="survey-list-container mx-auto w-[clamp(800px,calc(100%-64px),1200px)] pt-[70px] pb-[36px]"
    :style="{ minHeight: `calc(100vh - ${footerHeight}px)` }"
  >
    <CommonBreadcrumb :items="surveyBreadcrumbItems" />

    <div class="flex w-full justify-between gap-[20px] max-[1000px]:flex-col">
      <div class="w-full max-w-[752px] min-w-0 flex-1 max-[1000px]:max-w-full">
        <div v-for="(item, index) in surveyListItems" :key="item.sid || item.code">
          <SurveyListItem :item="item" :index="index" />
        </div>

        <div class="mt-[32px] flex justify-center">
          <CommonPagination
            :current-page="currentPage"
            :total="surveyListTotal"
            :page-size="pageSize"
            :page-route="pageRoute"
          />
        </div>
      </div>

      <div class="flex w-[384px] min-w-0 shrink-0 flex-col gap-[20px] max-[1000px]:w-full max-[1000px]:flex-row">
        <SurveyListTraders :list="recentTraders" />
        <div class="sticky top-[90px] max-[1000px]:hidden">
          <CommonDownload :download-links="downloadLinks" />
        </div>
      </div>
    </div>
  </div>
</template>
